import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/* ============================================
   APPLE VISION PRO CARD SYSTEM (REFINED v2)
   Background: 2% opacity translucent (lighter glass)
   Blur: 12px (soft frosted effect)
   Border: 1px hairline, very subtle (~3% opacity)
   Radius: 22px (20-24px globally)
   Shadow: soft, diffused, subtle inner top glow
   ============================================ */
const cardBase = [
  "relative",
  "text-card-foreground",
  "rounded-[22px]",
  "overflow-hidden",
  "min-w-0",
  "glass-card-base", /* Theme-aware glass background utility with hover */
  "!bg-[hsl(0_0%_100%)]", /* Force pure white in base (overridden by dark) */
  "dark:!bg-transparent", /* Allow glass effect only in dark mode */
  "backdrop-blur-0",
  "dark:backdrop-blur-[12px]",
  // Default depth for panels/cards (light + dark). Uses design token shadows.
  "shadow-[var(--shadow-card)]",
  // Hover lift behavior built-in to all cards
  "transition-all duration-200 ease-out",
  "hover:translate-y-[-2px]",
  "hover:shadow-[var(--shadow-card-hover)]",
].join(" ");

const cardVariants = cva(cardBase, {
  variants: {
    variant: {
      default: "",
      solid: "!bg-card backdrop-blur-none", /* For when you need opaque */
      /* All glass variants use glass-card-base which is theme-aware.
         Dark: translucent white glass with blur
         Light: solid white with subtle shadow */
      elevated: "shadow-[var(--shadow-lg)]",
      glass: "",
      "glass-light": "",
      "glass-premium": "hover:translate-y-[-1px] hover:shadow-[var(--shadow-card-hover)]",
      premium: "relative overflow-hidden",
      "ai-powered": "border-primary/10 shadow-[0_0_16px_hsl(220_90%_60%/0.08)]",
      "neon-accent": "border-primary/10 shadow-[0_0_16px_hsl(220_90%_60%/0.08)] hover:translate-y-[-1px]",
      interactive: "hover:translate-y-[-1px] hover:shadow-[var(--shadow-card-hover)] hover:border-[hsl(220_30%_50%/0.05)] cursor-pointer",
      floating: "shadow-[var(--shadow-lg)]",
      neumorphic: "",
      parallax: "",
      web3: "web3-glass-card web3-card-hover",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, ...props }, ref) => (
    <div ref={ref} data-slot="card" className={cn(cardVariants({ variant }), className)} {...props} />
  )
);
Card.displayName = "Card";

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col space-y-2 p-6", className)} {...props} />
  ),
);
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3 ref={ref} className={cn("text-[18px] font-medium leading-[1.5] tracking-tight", className)} {...props} />
  ),
);
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn("text-[13px] text-muted-foreground/70 mt-1 leading-[1.5]", className)} {...props} />
  ),
);
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />,
);
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props} />
  ),
);
CardFooter.displayName = "CardFooter";

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent, cardVariants };
